import { FC } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Grid,
  Surface,
  Text,
  ToggleSwitch,
} from "@zapier/design-system";
import { useAtom, useSetAtom } from "jotai";
import { useViewport } from "@xyflow/react";

import {
  showDebugAtom,
  showJotaiDevToolsAtom,
  shouldShowStepIdsAtom,
  clearCanvasSetter,
  useCopilotDrawerState,
} from "@zapier/canvas-state";
import { useCenterAndZoomOnNode } from "../../hooks/useCenterAndZoomOnNode";

import styles from "./DevTools.module.css";

export const DebugTabContent: FC = () => {
  const [shouldShowStepIds, setShouldShowStepIds] = useAtom(
    shouldShowStepIdsAtom,
  );
  const [showJotaiDevTools, setShowJotaiDevTools] = useAtom(
    showJotaiDevToolsAtom,
  );
  const [showAiToolkitDebug, setShowAiToolkitDebug] = useAtom(showDebugAtom);
  const clearCanvas = useSetAtom(clearCanvasSetter);
  const { centerAndZoomOnNode } = useCenterAndZoomOnNode();
  const [, setIsCopilotOpen] = useCopilotDrawerState();

  const viewport = useViewport();

  const handleClearCanvas = () => {
    void clearCanvas(centerAndZoomOnNode);
    // Defer to next tick to prevent "setState during render" error
    // useCopilotDrawerState causes parent (CanvasLayout) updates during render
    setTimeout(() => setIsCopilotOpen(false), 0);
  };
  return (
    <div className={styles.devtoolsSectionWrapper}>
      <Alert title="" status="info" variant="compact">
        <AlertDescription>
          <Text type="MinimalPrint3">General debugging features.</Text>
        </AlertDescription>
      </Alert>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.flexRow}>
            <Text type="Body3">Show step IDs</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={shouldShowStepIds}
                onChange={() => setShouldShowStepIds((prev) => !prev)}
              />
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div
          className={`${styles.devtoolsInner} ${styles.flexRowSpaceBetween}`}
        >
          <Text type="Body3" tag="div">
            Start fresh with 1 blank node
          </Text>
          <Button size="small" variant="primary" onPress={handleClearCanvas}>
            Clear canvas
          </Button>
        </div>
      </Surface>
      {process.env.NODE_ENV !== "production" && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <div className={styles.flexRow}>
              <Text type="Body3">Show Jotai DevTools</Text>
              <div className={styles.toggleWrapper}>
                <ToggleSwitch
                  checked={showJotaiDevTools}
                  onChange={() => setShowJotaiDevTools((prev) => !prev)}
                />
              </div>
            </div>
          </div>
        </Surface>
      )}
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <div className={styles.flexRow}>
            <Text type="Body3">AI Toolkit Debug</Text>
            <div className={styles.toggleWrapper}>
              <ToggleSwitch
                checked={showAiToolkitDebug}
                onChange={() => setShowAiToolkitDebug((prev) => !prev)}
              />
            </div>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Simulate Error</Text>
          <Text type="SmallPrint1" tag="div">
            Manually throw an error, so you can trace it in Datadog.
          </Text>
          <div className={styles.devToolsContentWrapperFlex}>
            <Button
              variant="danger"
              size="small"
              onPress={() => {
                throw new Error("Simulated error");
              }}
            >
              Throw Error
            </Button>
          </div>
        </div>
      </Surface>
      <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
        <div className={styles.devtoolsInner}>
          <Text type="Body3">Viewport</Text>
          <div className={styles.viewportWrapper}>
            <Grid columns={3}>
              <div className={styles.viewportCell}>
                <strong>x:</strong> {viewport.x.toFixed(1)}
              </div>
              <div className={styles.viewportCell}>
                <strong>y:</strong> {viewport.y.toFixed(1)}
              </div>
              <div className={styles.viewportCell}>
                <strong>zoom:</strong> {viewport.zoom.toFixed(1)}
              </div>
            </Grid>
          </div>
        </div>
      </Surface>
    </div>
  );
};
